import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import FamilyManagement from './FamilyManagement.tsx';
import RanchGirlManagement from './RanchGirlManagement.tsx';
import HorseManagement from './HorseManagement.tsx';
import HorseReports from './HorseReports.tsx';
import NotificationManagement from './NotificationManagement.tsx';
import InvoiceManagement from './InvoiceManagement.tsx';
import FinancialReports from './FinancialReports.tsx';
import EventManagement from './EventManagement.tsx';
import DocumentManagement from './DocumentManagement.tsx';
import BoardMemberManagement from './BoardMemberManagement.tsx';
import AlumniManagement from './AlumniManagement.tsx';
import AdminSidebar from './AdminSidebar.tsx';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';
import AdminSettings from './AdminSettings.tsx';
import UserActivity from './UserActivity.tsx';
import TextMessaging from './TextMessaging.tsx';
import GirlsCornerManagement from './GirlsCornerManagement.tsx';
import AdminManagement from './AdminManagement.tsx';
import DutyManagement from './DutyManagement.tsx';

// Helper function to format date string without timezone conversion
const formatDateSafe = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline?: string;
  createdBy: string;
  createdAt: string;
  rsvps: any[];
}

interface DashboardStats {
  activeFamilies: number;
  totalRanchGirls: number;
  upcomingEvents: number;
  outstandingInvoices: {
    totalAmount: number;
    count: number;
  };
}

interface RecentActivity {
  id: string;
  type: 'payment' | 'invoice' | 'notification' | 'event';
  title: string;
  description: string;
  amount?: number;
  timestamp: string;
  icon: AppIconName;
  color: string;
}

export default function AdminDashboard() {
  const { user, logout, retryAuth } = useAuth();
  const navigate = useNavigate();

  // Helper function for authenticated API calls
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 429) {
      console.warn('Rate limited for request:', url, '- please wait before retrying');
      throw new Error('Rate limited - too many requests');
    }

    if (response.status === 401) {
      // Don't logout automatically, just try to retry auth
      console.warn('Authentication failed for request:', url, '- attempting retry');
      retryAuth();
      throw new Error('Authentication failed - retry initiated');
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // Handle JSON parsing errors for rate limit responses
    try {
      return await response.json();
    } catch (parseError) {
      console.error('Failed to parse JSON response for:', url, parseError);
      throw new Error('Invalid response format');
    }
  };
  
  // Check if user has permission for a specific section
  const hasPermission = (sectionId: string) => {
    // Super admins have access to everything
    if (user?.isSuperAdmin) return true;
    
    // Regular admins need explicit permission
    if (user?.adminPermissions) {
      return user.adminPermissions.some(p => p.permissionKey === sectionId);
    }
    
    return false;
  };
  
  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState<DashboardStats>({ 
    activeFamilies: 0, 
    totalRanchGirls: 0,
    upcomingEvents: 0,
    outstandingInvoices: { totalAmount: 0, count: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  useEffect(() => {
    if (activeView === 'dashboard') {
      fetchDashboardStats();
      fetchRecentActivities();
    }
  }, [activeView]);

  const fetchUpcomingEvents = async () => {
    try {
      const data = await authenticatedFetch('/api/events?upcoming=true');
      if (data.success) {
        setUpcomingEvents(data.data);
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return [];
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch families data
      const familiesData = await authenticatedFetch('/api/families');
      const activeFamilies = familiesData.success ? 
        familiesData.data.filter((family: any) => family.membershipStatus === 'active').length : 0;

      // Count total ranch girls from families
      let totalRanchGirls = 0;
      if (familiesData.success) {
        familiesData.data.forEach((family: any) => {
          if (family.ranchGirls) {
            totalRanchGirls += family.ranchGirls.filter((girl: any) => girl.isActive).length;
          }
        });
      }

      // Fetch all invoices and filter for outstanding ones (sent or overdue)
      let outstandingInvoices = { totalAmount: 0, count: 0 };
      try {
        const invoicesData = await authenticatedFetch('/api/invoices');
        if (invoicesData.success) {
          const outstandingData = invoicesData.data.filter((invoice: any) => 
            invoice.status === 'sent' || invoice.status === 'overdue'
          );
          outstandingInvoices.count = outstandingData.length;
          outstandingInvoices.totalAmount = outstandingData.reduce((sum: number, invoice: any) => 
            sum + parseFloat(invoice.amount), 0
          );
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
      }

      // Fetch upcoming events
      const events = await fetchUpcomingEvents();

      setStats({
        activeFamilies,
        totalRanchGirls,
        upcomingEvents: events.length,
        outstandingInvoices
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      const activities: RecentActivity[] = [];

      // Fetch recent payments
      try {
        const paymentsData = await authenticatedFetch('/api/payments/history?status=succeeded');
        if (paymentsData.success) {
          const recentPayments = paymentsData.data.slice(0, 5);
          recentPayments.forEach((payment: any) => {
            activities.push({
              id: `payment-${payment.id}`,
              type: 'payment',
              title: 'Payment Received',
              description: `${payment.family?.familyName || 'Family'} - ${payment.invoice?.title || 'Payment'}`,
              amount: parseFloat(payment.amount),
              timestamp: payment.paymentDate || payment.createdAt,
              icon: 'invoices',
              color: 'green'
            });
          });
        }
      } catch (error) {
        console.error('Error fetching recent payments:', error);
      }

      // Fetch recent invoices
      try {
        const invoicesData = await authenticatedFetch('/api/invoices');
        if (invoicesData.success) {
          const recentInvoices = invoicesData.data
            .filter((invoice: any) => invoice.status !== 'draft')
            .slice(0, 3);
          
          recentInvoices.forEach((invoice: any) => {
            activities.push({
              id: `invoice-${invoice.id}`,
              type: 'invoice',
              title: 'Invoice Created',
              description: `${invoice.family?.familyName || 'Family'} - ${invoice.title}`,
              amount: parseFloat(invoice.amount),
              timestamp: invoice.createdAt,
              icon: 'documents',
              color: 'yellow'
            });
          });
        }
      } catch (error) {
        console.error('Error fetching recent invoices:', error);
      }

      // Fetch recent notifications
      try {
        const notificationsData = await authenticatedFetch('/api/notifications');
        if (notificationsData.success) {
          const recentNotifications = notificationsData.data.slice(0, 3);
          recentNotifications.forEach((notification: any) => {
            activities.push({
              id: `notification-${notification.id}`,
              type: 'notification',
              title: 'Notification Sent',
              description: `${notification.title} - ${notification.targetAudience === 'all' ? 'All families' : notification.targetAudience}`,
              timestamp: notification.createdAt,
              icon: 'notifications',
              color: 'blue'
            });
          });
        }
      } catch (error) {
        console.error('Error fetching recent notifications:', error);
      }

      // Fetch recent events
      try {
        const eventsData = await authenticatedFetch('/api/events');
        if (eventsData.success) {
          const recentEvents = eventsData.data
            .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .slice(0, 3);
          
          recentEvents.forEach((event: any) => {
            activities.push({
              id: `event-${event.id}`,
              type: 'event',
              title: 'Event Created',
              description: `${event.title} - ${formatDateSafe(event.date)} at ${event.time}`,
              timestamp: event.createdAt,
              icon: 'events',
              color: 'purple'
            });
          });
        }
      } catch (error) {
        console.error('Error fetching recent events:', error);
      }

      // Sort all activities by timestamp (most recent first) and take top 5
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentActivities(activities.slice(0, 5));

    } catch (error) {
      console.error('Error fetching recent activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeView) {
      case 'families':
        return <FamilyManagement />;
      case 'ranchgirls':
        return <RanchGirlManagement />;
      case 'horses':
        return <HorseManagement />;
      case 'horse-reports':
        return <HorseReports />;
      case 'boardmembers':
        return <BoardMemberManagement />;
      case 'alumni':
        return <AlumniManagement />;
      case 'events':
        return <EventManagement />;
      case 'duties':
        return <DutyManagement />;
      case 'notifications':
        return <NotificationManagement />;
      case 'textmessaging':
        return <TextMessaging />;
      case 'girlscorner':
        return <GirlsCornerManagement />;
      case 'documents':
        return <DocumentManagement />;
      case 'invoices':
        return <InvoiceManagement />;
      case 'reports':
        return <FinancialReports />;
      case 'settings':
        return <AdminSettings />;
      case 'user-activity':
        return <UserActivity />;
      case 'admin-management':
        return <AdminManagement />;
      default:
        // Dashboard home view
        return (
          <div className="px-12 py-10">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">Welcome back, {user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-gray-400 mt-1">v1.1.1 - Database Protection Active</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {hasPermission('families') && (
                <div 
                  className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setActiveView('families')}
                >
                  <h3 className="text-sm font-medium text-gray-500">Active Families</h3>
                  {loading ? (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-ggaa-green mt-2">{stats.activeFamilies}</p>
                      <p className="text-xs text-ggaa-green/70 mt-1">Click to manage →</p>
                    </>
                  )}
                </div>
              )}

              {hasPermission('invoices') && (
                <div 
                  className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setActiveView('invoices')}
                >
                  <h3 className="text-sm font-medium text-gray-500">Outstanding Invoices</h3>
                  {loading ? (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
                      <span className="ml-2 text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-yellow-600 mt-2">
                        ${stats.outstandingInvoices.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-yellow-600/70 mt-1">
                        {stats.outstandingInvoices.count} invoice{stats.outstandingInvoices.count !== 1 ? 's' : ''}
                      </p>
                    </>
                  )}
                </div>
              )}

              {hasPermission('events') && (
                <div 
                  className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setActiveView('events')}
                >
                  <h3 className="text-sm font-medium text-gray-500">Upcoming Events</h3>
                  {loading ? (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-blue-600 mt-2">{stats.upcomingEvents}</p>
                      <p className="text-xs text-blue-600/70 mt-1">Click to manage →</p>
                    </>
                  )}
                </div>
              )}

              {hasPermission('ranchgirls') && (
                <div 
                  className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setActiveView('ranchgirls')}
                >
                  <h3 className="text-sm font-medium text-gray-500">Total Ranch Girls</h3>
                  {loading ? (
                    <div className="flex items-center mt-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-purple-600 mt-2">{stats.totalRanchGirls}</p>
                      <p className="text-xs text-purple-600/70 mt-1">Click to manage →</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Recent Activity Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-ggaa-green/20">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AppIcon name="reports" className="w-5 h-5 text-ggaa-green" />
                Recent Activity
              </h3>
              {activitiesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
                  <span className="ml-3 text-gray-600">Loading activities...</span>
                </div>
              ) : recentActivities.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No recent activity</p>
              ) : (
                <div className="space-y-4">
                  {recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white border border-gray-200 text-ggaa-green">
                        <AppIcon name={activity.icon} className="w-5 h-5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                        <p className="text-sm text-gray-600 truncate">{activity.description}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(activity.timestamp).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {activity.amount && (
                        <span className="text-sm font-medium text-ggaa-green">
                          ${activity.amount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <AdminSidebar 
        currentView={activeView} 
        onNavigate={setActiveView} 
        onLogout={handleLogout}
        user={user} 
      />
      <div className="flex-1 overflow-y-auto pt-16 lg:pt-0">
        {renderContent()}
      </div>
    </div>
  );
} 